import { Modal } from '../../ui/Modal.tsx';
import { AISLES, AISLE_LABELS, OTHER, type Aisle } from './aisleLabels.ts';

interface AislePickerProps {
  /** The name being filed — it is the name that gets the aisle, not the row. */
  name: string;

  /** Where it currently sits, whether the household chose that or the shipped table did. */
  current: Aisle;

  /** Whether the household has overruled the table for this name, rather than agreed with it. */
  corrected: boolean;

  onChoose: (aisle: Aisle) => void;

  /** Back to whatever the shipped table says. Only offered when there is a correction. */
  onClear: () => void;

  onClose: () => void;
}

/**
 * Which aisle a name belongs in, asked as a sheet.
 *
 * A sheet rather than a cycling dot, unlike the assignment beside it: nine values is too
 * many to tap through, and unlike an assignment this is a thing somebody is correcting
 * because it is *wrong* — so the right answer wants to be reachable in one tap rather than
 * up to nine. `PhotoSourceSheet` is the precedent, and the argument is the same: the sheet
 * is the answer to the tap, not an interruption of one.
 *
 * It says the name at the top because that is what it is about. Correcting "milk" from the
 * milk on this week's list moves every milk on every list the household has, now and next
 * year, and a sheet titled only "Aisle" would be quietly claiming to do much less than it
 * does.
 *
 * <b>Other is offered, at the bottom and set apart.</b> It is not an aisle, but choosing it
 * is a real statement — "the app guessed and it guessed wrong, and none of these is right
 * either" — which is a different thing from never having been asked, and the only way to
 * get a name *out* of an aisle it does not belong in.
 */
/** The eight aisles then the unknown bucket, which is the order the sheet offers them in. */
const OPTIONS: Aisle[] = [...AISLES, OTHER];

export function AislePicker({
  name,
  current,
  corrected,
  onChoose,
  onClear,
  onClose,
}: AislePickerProps) {
  return (
    <Modal title={name} variant="sheet" onClose={onClose}>
      {/* The first sheet in the app whose rows are values rather than actions, so the icon
          slot holds a tick on one row and nothing on the other eight — see the modifier's
          rule for why those eight lose their box. */}
      <div className="menu menu--choices">
        {OPTIONS.map((aisle) => (
          <button
            key={aisle}
            type="button"
            className={
              aisle === current
                ? 'menu__item menu__item--icon menu__item--selected'
                : 'menu__item menu__item--icon'
            }
            // The tick is decoration; the state is what a screen reader is told, and it is
            // told it the way a list of one-of-many choices should be.
            aria-current={aisle === current ? 'true' : undefined}
            onClick={() => onChoose(aisle)}
          >
            <span className="menu__icon" aria-hidden="true">
              {aisle === current && (
                <svg viewBox="0 0 16 16" width="18" height="18" focusable="false">
                  <path
                    d="M3 8.5l3.2 3.2L13 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span className="menu__label">{AISLE_LABELS[aisle]}</span>
          </button>
        ))}

        {/* Only when there is something to undo. Offered against a name the household has
            never corrected, this would be a control that does nothing — and it would imply
            the shipped table's answer was somebody's choice, which is the opposite of what
            it is. */}
        {corrected && (
          <button
            type="button"
            className="menu__item menu__item--quiet menu__item--centred"
            onClick={onClear}
          >
            <span className="menu__label">Use the app&rsquo;s guess</span>
          </button>
        )}

        <button
          type="button"
          className="menu__item menu__item--quiet menu__item--centred"
          onClick={onClose}
        >
          <span className="menu__label">Cancel</span>
        </button>
      </div>
    </Modal>
  );
}
